import Component from '@glimmer/component';
import { service } from '@ember/service';
import { VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import { Button } from 'tio-ui/components/buttons';
import SectionHeader from 'tio-employee/components/reimagined/section-header';
import type { DataSourceInfo } from 'tio-employee/services/reimagined-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type PartnerService from 'tio-employee/services/partner';
import {
  PhEnvelope,
  PhPhone,
  PhChatCircleDots,
  PhQuestion,
  PhBookOpen,
  PhArrowRight,
  PhCalendar,
  PhRobot,
} from 'ember-phosphor-icons';

interface ContactModel {
  dataSourceInfo: DataSourceInfo;
}

interface ContactSignature {
  Args: {
    model: ContactModel;
  };
}

interface FaqItem {
  question: string;
  answer: string;
}

const COMMON_FAQS: FaqItem[] = [
  {
    question: 'How do I add my student loans?',
    answer:
      'Go to the Onboarding page or Documents section and upload your loan statements or NSLDS file from StudentAid.gov. You can upload a recent loan servicer statement or download your NSLDS file for automatic loan detection.',
  },
  {
    question: 'When will I receive my employer contribution?',
    answer:
      'Employer contributions are typically processed monthly on the same schedule as payroll. Check the Finance section for your next expected payment date.',
  },
  {
    question: 'How do I apply for tuition assistance?',
    answer:
      'Navigate to Benefits > Tuition Assistance and click "Start New Application." You\'ll need your program details, course information, and any required approvals.',
  },
  {
    question: 'What is the Section 127 benefit?',
    answer:
      'Section 127 of the IRS code allows your employer to contribute up to $5,250 per year tax-free toward your education expenses, including student loan repayment.',
  },
  {
    question: 'How do I reset my password?',
    answer:
      'Click "Forgot Password" on the login page, enter your email, and follow the reset link sent to your inbox. You can also change your password from the Profile page.',
  },
];

export default class ContactPage extends Component<ContactSignature> {
  @service declare reimaginedData: ReimaginedDataService;
  @service declare partner: PartnerService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get supportEmail() {
    const support = this.partner.supportModule;
    if (!support || support?.tioEmail?.isEnabled) {
      return 'support@tuition.io';
    }
    return support?.partnerEmail ?? 'support@tuition.io';
  }

  get faqs() {
    return COMMON_FAQS;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="gap-8 pb-12">
      {{! Hero Section }}
      <div
        class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-sky-600 via-sky-700 to-blue-800 p-8 text-white"
      >
        <div class="absolute top-0 right-0 -mt-8 -mr-8 h-48 w-48 rounded-full bg-white/5"></div>
        <div class="absolute bottom-0 left-0 -mb-12 -ml-12 h-64 w-64 rounded-full bg-white/5"></div>
        <div class="relative z-10 text-center">
          <div
            class="flex h-16 w-16 items-center justify-center rounded-xl bg-white/20 mx-auto mb-4"
          >
            <PhChatCircleDots class="h-9 w-9 text-white" />
          </div>
          <Heading @level={{2}} class="text-white text-2xl font-bold mb-2">
            How Can We Help?
          </Heading>
          <Text class="text-sky-100 max-w-lg mx-auto">
            Our support team is here to help you make the most of your financial benefits. Reach out
            through any of the channels below.
          </Text>
        </div>
      </div>

      {{! Contact Methods }}
      <SectionHeader
        @icon={{PhEnvelope}}
        @title="Get In Touch"
        @subtitle="Choose your preferred way to reach us"
        @iconClass="text-blue-500"
      />
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 text-center hover:shadow-lg transition-shadow"
        >
          <div
            class="flex h-14 w-14 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/30 mx-auto mb-4"
          >
            <PhEnvelope class="h-7 w-7 text-blue-600" />
          </div>
          <Heading @level={{4}} class="font-semibold mb-2">Email Us</Heading>
          <Text class="text-gray-600 dark:text-gray-400 text-sm mb-4">
            Send us a detailed message and we'll respond within 24 hours.
          </Text>
          <a
            href="mailto:{{this.supportEmail}}"
            class="text-blue-600 hover:text-blue-800 dark:text-blue-400 font-medium text-sm"
          >
            {{this.supportEmail}}
          </a>
        </div>

        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 text-center hover:shadow-lg transition-shadow"
        >
          <div
            class="flex h-14 w-14 items-center justify-center rounded-xl bg-green-100 dark:bg-green-900/30 mx-auto mb-4"
          >
            <PhPhone class="h-7 w-7 text-green-600" />
          </div>
          <Heading @level={{4}} class="font-semibold mb-2">Call Us</Heading>
          <Text class="text-gray-600 dark:text-gray-400 text-sm mb-3">
            Mon-Fri, 8am-6pm CT
          </Text>
          <a
            href="tel:18556855100"
            class="text-green-600 hover:text-green-800 dark:text-green-400 font-medium text-sm"
          >
            1-855-685-5100
          </a>
        </div>

        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 text-center hover:shadow-lg transition-shadow"
        >
          <div
            class="flex h-14 w-14 items-center justify-center rounded-xl bg-purple-100 dark:bg-purple-900/30 mx-auto mb-4"
          >
            <PhChatCircleDots class="h-7 w-7 text-purple-600" />
          </div>
          <Heading @level={{4}} class="font-semibold mb-2">Live Chat</Heading>
          <Text class="text-gray-600 dark:text-gray-400 text-sm mb-4">
            Chat with a real person for instant help.
          </Text>
          <Button @intent="primary" @appearance="outlined">
            Start Chat
          </Button>
        </div>
      </div>

      {{! AI Advisor Promo }}
      <div
        class="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 rounded-2xl p-6 flex items-center gap-6"
      >
        <div
          class="flex h-14 w-14 items-center justify-center rounded-xl bg-violet-100 dark:bg-violet-900/30 shrink-0"
        >
          <PhRobot class="h-7 w-7 text-violet-600" />
        </div>
        <div class="flex-1">
          <Heading @level={{4}} class="font-semibold mb-1">Try Our AI Advisor</Heading>
          <Text class="text-gray-600 dark:text-gray-400 text-sm">
            Get instant answers about your benefits, loans, and financial strategies 24/7.
          </Text>
        </div>
        <Button @intent="primary">
          Ask AI
          <PhArrowRight class="ml-2 h-4 w-4" />
        </Button>
      </div>

      {{! FAQs }}
      <SectionHeader
        @icon={{PhQuestion}}
        @title="Common Questions"
        @subtitle="Quick answers to frequent questions"
        @iconClass="text-sky-500"
      />
      <div class="space-y-3">
        {{#each this.faqs as |faq|}}
          <div
            class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5"
          >
            <div class="flex items-start gap-3">
              <div
                class="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-100 dark:bg-sky-900/30 shrink-0 mt-0.5"
              >
                <PhQuestion class="h-4 w-4 text-sky-600" />
              </div>
              <div>
                <Heading @level={{4}} class="font-semibold text-gray-900 dark:text-white mb-2">
                  {{faq.question}}
                </Heading>
                <Text class="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  {{faq.answer}}
                </Text>
              </div>
            </div>
          </div>
        {{/each}}
      </div>

      {{! Help Resources }}
      <SectionHeader
        @icon={{PhBookOpen}}
        @title="Help Resources"
        @subtitle="Self-service tools and guides"
        @iconClass="text-amber-500"
      />
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5"
        >
          <div class="flex items-center gap-3 mb-3">
            <div
              class="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/30"
            >
              <PhBookOpen class="h-5 w-5 text-amber-600" />
            </div>
            <Heading @level={{4}} class="font-semibold">Glossary</Heading>
          </div>
          <Text class="text-gray-600 dark:text-gray-400 text-sm">
            Look up financial terms and student loan jargon.
          </Text>
        </div>

        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5"
        >
          <div class="flex items-center gap-3 mb-3">
            <div
              class="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-100 dark:bg-teal-900/30"
            >
              <PhCalendar class="h-5 w-5 text-teal-600" />
            </div>
            <Heading @level={{4}} class="font-semibold">Coaching</Heading>
          </div>
          <Text class="text-gray-600 dark:text-gray-400 text-sm">
            Schedule a session with a certified student loan coach.
          </Text>
        </div>

        <div
          class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5"
        >
          <div class="flex items-center gap-3 mb-3">
            <div
              class="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/30"
            >
              <PhQuestion class="h-5 w-5 text-indigo-600" />
            </div>
            <Heading @level={{4}} class="font-semibold">Knowledge Base</Heading>
          </div>
          <Text class="text-gray-600 dark:text-gray-400 text-sm">
            Browse articles on student loans, benefits, and financial planning.
          </Text>
        </div>
      </div>
    </VStack>
  </template>
}
